import { useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import { api } from '../services/api';
import { Panel, PanelHeader, PanelBody } from './ui/Panel';
import Pill from './ui/Pill';
import Button from './ui/Button';
import Modal from './ui/Modal';
import { ErrorNotice, SkeletonRows } from './ui/States';
import Checklist from './Checklist';
import { money, PAYMENT_STATUS_LABEL, PAYMENT_TONE } from '../lib/programs';
import { AGREEMENT_STATUS, ParticipantStipend, STIPEND_EVIDENCE } from '../lib/stipends';

/** The member's Fellow stipends: route, agreement, ledger line and steps. Admin only (amounts; reads are audited). */
export default function MemberStipend({ userId }: { userId: string }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState<ParticipantStipend | null>(null);
  const list = useQuery<ParticipantStipend[]>(
    ['stipends', 'member', userId],
    async () => (await api.get(`/stipends/members/${userId}`)).data.data,
  );
  if (list.data && !list.data.length) return null;
  return (
    <Panel>
      <PanelHeader title="Stipend" />
      <PanelBody className="pt-2">
        {list.isLoading ? (
          <SkeletonRows rows={2} />
        ) : list.error ? (
          <ErrorNotice>Could not load the stipend.</ErrorNotice>
        ) : (
          <ul className="text-table">
            {list.data?.map((s) => (
              <li key={s.participantId} className="py-2 border-b border-line last:border-0">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    Fellows {s.cycle.label} · {s.route ? s.routes[s.route] : '—'}
                  </div>
                  {s.ledger ? (
                    <Pill tone={PAYMENT_TONE[s.ledger.status]}>{PAYMENT_STATUS_LABEL[s.ledger.status]}</Pill>
                  ) : null}
                </div>
                <div className="text-small text-ink-2 mt-1">
                  {s.ledger ? `${money(s.ledger.amountCents)} · FY ${s.ledger.fiscalYear}` : 'No ledger line'}
                  {s.fellowKind === 'ecr'
                    ? ' · ECR (recipient)'
                    : s.supervisor
                      ? ` · supervisor ${s.supervisor.name}`
                      : ''}
                  {s.agreement
                    ? ` · agreement ${AGREEMENT_STATUS[s.agreement.status] ?? s.agreement.status}${s.agreement.participants.length > 1 ? ` (shared with ${s.agreement.participants.length - 1} other)` : ''}`
                    : ''}
                  {s.periodEnd ? ` · funds until ${s.periodEnd}` : ''}
                </div>
                <div className="flex gap-3 mt-2">
                  <Button size="sm" variant="outline" onClick={() => setOpen(s)}>
                    Steps
                  </Button>
                  <Link
                    className="text-small underline self-center"
                    to={`/stipends?cycle=${s.cycle.id}&participant=${s.participantId}`}
                  >
                    Cohort grid
                  </Link>
                </div>
              </li>
            ))}
          </ul>
        )}
      </PanelBody>
      {open ? (
        <Modal title={`Stipend: Fellows ${open.cycle.label}`} width={820} onClose={() => setOpen(null)}>
          <Checklist
            subjectType="participant"
            subjectId={open.participantId}
            mutable
            evidenceFields={STIPEND_EVIDENCE}
            onChange={() => queryClient.invalidateQueries(['stipends', 'member', userId])}
          />
        </Modal>
      ) : null}
    </Panel>
  );
}
